import React from "react";
import { Col } from "react-bootstrap";

import Control from "../../../assets/vfx/controls/control";
import Form from "../../../assets/vfx/form/form";
import {
  IControlStyles,
  PageContent,
  useControlStyles,
} from "../../../assets/layouts";
import { SearchContainer } from "../../../assets/vfx/search/searchContainer";

import { SubscriptionService } from "./subscriptionService";

import { Constants } from "../../../assets/utils/lib/constants";
import { GRID_ACTION_TYPES, defaultSearchQuery } from "../../../assets/types";
import { SearchDto, SearchOperator } from "../../../assets/utils/lib/searchDto";
import { ControlDataGridColumnProps } from "../../../assets/vfx/controls/components/datagrid/controlDataGrid";
import {
  createActionColumn,
  createLayoutOptions,
} from "../../../assets/configuration/gridConfig";
import { GridSubscriptionDto, PlanDto } from "../../../services/dto";
import { DropdownOption } from "../../../assets/vfx/controls/components/dropdown/controlDropdown";

interface SubscriptionPageProps {
  plans: PlanDto[];
  controlStyles?: IControlStyles;
  onCustomEvent?: (actionType: string, dataId: string) => void;
}
interface SubscriptionSearch {
  name: string;
  planId: string;
}

const DATA_ID_FIELD = "id";

const searchInitialValues: SubscriptionSearch = {
  name: Constants.emptyString,
  planId: Constants.emptyGuid,
};

const createGridColumns = (): ControlDataGridColumnProps[] => [
  {
    data: DATA_ID_FIELD,
    title: "ID",
    visible: false,
    columnType: "number",
    alignment: "right",
    sortable: false,
  },
  {
    data: "name",
    title: "Name",
    visible: true,
    columnType: "text",
    alignment: "left",
    width: "18%",
  },
  {
    data: "description",
    title: "Description",
    visible: true,
    columnType: "text",
    alignment: "left",
    width: "34%",
  },
  {
    data: "activationDate",
    title: "Activation Date",
    visible: true,
    columnType: "date",
    alignment: "left",
    width: "24%",
  },
];

const convertToDropdownOptions = (plans: PlanDto[]): DropdownOption[] => {
  return plans.map((plan) => ({
    key: plan.id,
    value: plan.id,
    display: plan.name ?? Constants.emptyString,
  }));
};

export const SubscriptionPage = (props: SubscriptionPageProps) => {
  const styles = props.controlStyles ?? useControlStyles();
  const [searchQuery, setSearchQuery] =
    React.useState<SearchDto>(defaultSearchQuery);

  const subscriptionGridQuery = SubscriptionService.gridQuery(searchQuery);
  const isGridLoading =
    subscriptionGridQuery.isLoading || subscriptionGridQuery.isFetching;

  const subscriptions: GridSubscriptionDto[] =
    subscriptionGridQuery.data?.result ?? [];

  const hasData: boolean = subscriptions.length > 0;
  const gridColumns: ControlDataGridColumnProps[] = createGridColumns();
  const actionColumn = createActionColumn(styles, DATA_ID_FIELD, [
    GRID_ACTION_TYPES.EDIT,
    GRID_ACTION_TYPES.VIEW,
  ]);
  const layoutOptions = createLayoutOptions(hasData);

  const dropdownOptions: DropdownOption[] = convertToDropdownOptions(
    props.plans
  );

  const handleSearch = (values: SubscriptionSearch): void => {
    const filters = [];

    if (values.name.trim() !== Constants.emptyString) {
      filters.push({
        field: "name",
        operator: SearchOperator.Contains,
        value: values.name,
      });
    }

    if (values.planId !== Constants.emptyGuid) {
      filters.push({
        field: "planId",
        operator: SearchOperator.Equal,
        value: values.planId,
      });
    }

    if (filters.length > 0 && Array.isArray(defaultSearchQuery.filters)) {
      filters.push(...defaultSearchQuery.filters);
    }

    if (filters.length > 0) {
      setSearchQuery({
        ...defaultSearchQuery,
        filters,
      });
    }
  };

  const handleClear = (): void => {
    setSearchQuery((prev) => ({
      ...defaultSearchQuery,
      sorts: prev.sorts,
      pageSize: prev.pageSize ?? 10,
    }));
  };

  const handleNew = (): void =>
    props.onCustomEvent?.(GRID_ACTION_TYPES.NEW, Constants.emptyGuid);

  return (
    <PageContent title="Subscriptions">
      <SearchContainer
        initialValues={searchInitialValues}
        enableReinitialize
        onSearch={handleSearch}
        onNew={handleNew}
        onClear={handleClear}
      >
        <Col lg={4}>
          <Form.Text name="name" label="Name" />
        </Col>
        <Col lg={3} className="ms-2">
          <Form.Dropdown
            blankOption={{
              key: Constants.emptyGuid,
              value: Constants.emptyGuid,
              display: "Select Plan",
            }}
            options={dropdownOptions}
            name={"planId"}
            label={"Plan"}
          ></Form.Dropdown>
        </Col>
      </SearchContainer>
      <Control.DataGrid
        dataId={DATA_ID_FIELD}
        cols={gridColumns}
        data={subscriptions}
        actionCol={actionColumn}
        layoutOptions={layoutOptions}
        onCustomEvent={props.onCustomEvent}
        totalRecords={
          subscriptionGridQuery.data?.totalSize ?? subscriptions?.length ?? 0
        }
        query={searchQuery}
        loading={isGridLoading}
        onQueryChange={(patch) => {
          setSearchQuery((query) => ({
            ...query,
            ...patch,
            sorts: patch.sortBy ? [patch.sortBy] : query.sorts,
          }));
        }}
      />
    </PageContent>
  );
};
